import { Link, useNavigate } from "react-router-dom"; // Add useNavigate
import { useRef, useState } from "react";
import useThemeColor from '../../hooks/useThemeColor';
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";

const RenderCategoryRow = ({
  category,
  index,
  level = 0,
  parentIndex = null,
  grandParentIndex = null,
  categories,
  setCategories,
  visibleColumns,
  DeleteCategory,
  isChildBranch = false,
}) => {
  const themeColor = useThemeColor();
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [isExpanded, setIsExpanded] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const navigate = useNavigate();

  const hasSubcategories =
    category.subCategoriesList && category.subCategoriesList.length > 0;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCheckboxChange = (checked) => {
    const updateCheckedStatus = (categories, targetId, newCheckedState) => {
      return categories.map((cat) => {
        if (cat.rootCat_ID === targetId) {
          const updatedCategory = { ...cat, checked: newCheckedState };
          if (
            updatedCategory.subCategoriesList &&
            updatedCategory.subCategoriesList.length > 0
          ) {
            updatedCategory.subCategoriesList = updateCheckedStatus(
              updatedCategory.subCategoriesList,
              targetId,
              newCheckedState
            );
          }
          return updatedCategory;
        }
        if (cat.subCategoriesList && cat.subCategoriesList.length > 0) {
          return {
            ...cat,
            subCategoriesList: updateCheckedStatus(
              cat.subCategoriesList,
              targetId,
              newCheckedState
            ),
          };
        }
        return cat;
      });
    };
    setCategories((prev) =>
      updateCheckedStatus(prev, category.rootCat_ID, checked)
    );
  };

  const toggleCategoryStatus = async (rootCat_ID, status) => {
    try {
      await axiosInstance.post(APIs.POST_UPDATE_CATEGORY_STATUS, {
        CompID: CompID,
        RootCat_ID: rootCat_ID,
        RootCat_Publish: status,
      });
    } catch (error) {

      setCategories(categories);
    }

    const updateCategoryStatus = (categories, targetId) => {
      return categories.map((cat) => {
        if (cat.rootCat_ID === targetId) {
          return { ...cat, rootCat_Publish: !cat.rootCat_Publish };
        }
        if (cat.subCategoriesList && cat.subCategoriesList.length > 0) {
          return {
            ...cat,
            subCategoriesList: updateCategoryStatus(
              cat.subCategoriesList,
              targetId
            ),
          };
        }
        return cat;
      });
    };
    setCategories((prev) => updateCategoryStatus(prev, category.rootCat_ID));
  };

  const handleDragStart = (e) => {
    dragItem.current = { index, parentIndex, grandParentIndex };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify(dragItem.current)
    );
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragOverItem.current = { index, parentIndex, grandParentIndex };
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const dragData = JSON.parse(e.dataTransfer.getData("application/json"));
    if (!dragData || !dragOverItem.current) return;

    const {
      index: draggedIndex,
      parentIndex: draggedParentIndex,
      grandParentIndex: draggedGrandParentIndex,
    } = dragData;

    const {
      index: overIndex,
      parentIndex: overParentIndex,
      grandParentIndex: overGrandParentIndex,
    } = dragOverItem.current;

    if (
      draggedIndex === overIndex &&
      draggedParentIndex === overParentIndex &&
      draggedGrandParentIndex === overGrandParentIndex
    ) {
      resetDragState();
      return;
    }

    if (
      draggedParentIndex !== overParentIndex ||
      draggedGrandParentIndex !== overGrandParentIndex
    ) {

      resetDragState();
      return;
    }

    let itemsToReorder;
    if (parentIndex === null && grandParentIndex === null) {
      itemsToReorder = [...categories];
    } else if (grandParentIndex === null) {
      itemsToReorder = [...categories[parentIndex].subCategoriesList];
    } else {
      itemsToReorder = [
        ...categories[grandParentIndex].subCategoriesList[parentIndex]
          .subCategoriesList,
      ];
    }

    const [draggedItem] = itemsToReorder.splice(draggedIndex, 1);
    itemsToReorder.splice(overIndex, 0, draggedItem);

    const updatedItems = itemsToReorder.map((item, index) => ({
      ...item,
      rootCat_Orderby: index + 1,
    }));

    setCategories((prevCategories) => {
      const newCategories = [...prevCategories];

      if (parentIndex === null && grandParentIndex === null) {
        return updatedItems;
      } else if (grandParentIndex === null) {
        newCategories[parentIndex].subCategoriesList = updatedItems;
      } else {
        newCategories[grandParentIndex].subCategoriesList[
          parentIndex
        ].subCategoriesList = updatedItems;
      }

      return newCategories;
    });

    try {
      const sortUpdates = updatedItems.map((item) => ({
        rootCat_ID: item.rootCat_ID,
        rootCat_Orderby: item.rootCat_Orderby,
      }));

      await axiosInstance.post(
        APIs.POST_UPDATE_CATEGORY_SORT_ORDER,
        {
          CompID: CompID,
          Updates: sortUpdates,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {

      setCategories(categories);
    }

    resetDragState();
  };

  const resetDragState = () => {
    dragItem.current = null;
    dragOverItem.current = null;
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.opacity = "1";
      tr.style.backgroundColor = "";
    });
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });

    if (dragItem.current) {
      dragItem.current = null;
    }
    if (dragOverItem.current) {
      dragOverItem.current = null;
    }
  };

  const handleDeleteCategory = () => {
    DeleteCategory(category, level > 0);
  };

  return (
    <>
      <tr
        draggable={false}
        onDragStart={(e) => {
          // Only allow dragging from the grab icon
          if (!e.target.classList.contains('fa-bars')) {
            e.preventDefault();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={(e) => {
          if (
            e.target.closest(".toggle-switch") || // Toggle switch
            e.target.closest(".dropdown") || // Action dropdown
            e.target.closest(".fa-bars") || // Drag handle
            e.target.closest(".fa-chevron-down") || // Expand/collapse icon
            e.target.closest(".fa-chevron-right") || // Expand/collapse icon
            e.target.closest("input[type='checkbox']") || // Checkbox
            e.target.closest("button") || // All buttons including delete
            e.target.tagName === "A" // Links in dropdown
          ) {
            return;
          } else {
            if (isChildBranch) return;
            navigate(`/categories/updateCategory/${category.rootCat_ID}`);
          }
        }}
        style={{ cursor: isChildBranch ? "default" : "pointer" }} // Indicate the row is clickable
        className={`${parentIndex !== null ? "bg-light bg-opacity-50" : ""}`}
      >
        <td
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="align-middle"
          style={{ width: "50px" }}
        >
          <input
            type="checkbox"
            style={{ marginRight: "8px" }}
            checked={category.checked || false}
            onChange={(e) => handleCheckboxChange(e.target.checked)}
          />
        </td>

        <td

          className="align-middle"
        >
          <div className="d-flex align-items-center">
            {Array(level)
              .fill()
              .map((_, i) => (
                <div key={i} style={{ width: "20px" }}></div>
              ))}
            {hasSubcategories && (
              <div onClick={(e) => {
                e.stopPropagation();
                toggleExpand();
              }}
                className="me-2"
                style={{ cursor: "pointer", width: "20px" }}

              >
                <i
                  className={`fa ${isExpanded ? "fa-chevron-down" : "fa-chevron-right"
                    }`}
                ></i>
              </div>
            )}
            {!hasSubcategories && parentIndex !== null && (
              <div className="me-2" style={{ width: "20px" }}></div>
            )}
            {/* Icon - Hidden for now 
            <div
              className="rounded p-2 d-flex align-items-center justify-content-center me-2"
              style={{
                width: 40,
                height: 40,
                minWidth: 40,
                backgroundColor: "#d9d9d9",
              }}
              onClick={() =>
                navigate(`/categories/updateCategory/${category.rootCat_ID}`)
              }
            >
              <LazyLoadImage
                src={
                  category.rootCat_Image
                    ? category.rootCat_Image
                    : "/assets/img/fires.png"
                }
                alt={category.rootCat_Name}
                className="card-img-top m-2"
                style={{
                  height: "35px",
                  width: "35px",
                  objectFit: "contain",
                }}
              />
            </div>
            */}
            <span>{category.rootCat_Name}</span>
          </div>
        </td>

        {visibleColumns.sortOrder && (
          <td
            className="align-middle text-center"
            onClick={() => {
              if (isChildBranch) return;
              navigate(`/categories/updateCategory/${category.rootCat_ID}`);
            }}
          >
            {category.rootCat_Orderby}
          </td>
        )}
        {visibleColumns.status && (
          <td className="align-middle text-center">
            <div
              className={`toggle-switch ${category.rootCat_Publish ? "active" : ""
                }`}
            >
              <label className="switch">
                <input
                  type="checkbox"
                  checked={category.rootCat_Publish}
                  onChange={() =>
                    toggleCategoryStatus(
                      category.rootCat_ID,
                      category.rootCat_Publish
                    )
                  }
                />
                <span 
                  className="slider round"
                  style={{
                    backgroundColor: category.rootCat_Publish ? themeColor : '#ccc'
                  }}
                ></span>
              </label>
            </div>
          </td>
        )}
        {visibleColumns.products && (
          <td className="align-middle text-center">
            <Link
              to={`/items/manageItems/${category.rootCat_ID}`}
              className="text-decoration-none"
              style={{ 
                color: themeColor,
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = themeColor;
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = themeColor;
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              {category.products || 0}
            </Link>
          </td>
        )}
        {visibleColumns.addOnCategory && (
          <td className="align-middle text-center">
            {category.isAddOnCategory ? 'True' : 'False'}
          </td>
        )}

        <td className="align-middle text-center">
          <div className="d-flex justify-content-center gap-2">
            {/* Drag Handle */}
            <i
              className="fas fa-bars text-secondary"
              style={{ 
                cursor: "grab",
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
                border: '1px solid #6c757d',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease'
              }}
              draggable
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              title="Drag to reorder"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#6c757d';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6c757d';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            ></i>

            {/* Edit Icon */}
            {!isChildBranch && (
              <Link
                to={`/categories/updateCategory/${category.rootCat_ID}`}
                className="btn btn-sm btn-outline-primary"
                title="Edit Category"
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  border: `1px solid ${themeColor}`,
                  color: themeColor,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = themeColor;
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = themeColor;
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
              </Link>
            )}


            {/* Delete Icon */}
            {!isChildBranch && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent row click event
                  handleDeleteCategory();
                }}
                className="btn btn-sm btn-outline-danger"
                title="Delete Category"
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  border: '1px solid #dc3545',
                  color: '#dc3545',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc3545';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#dc3545';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
              </button>
            )}

          </div>
        </td>
      </tr>

      {hasSubcategories &&
        isExpanded &&
        category.subCategoriesList.map((subcategory, subIndex) => (
          <RenderCategoryRow
            key={subcategory.rootCat_ID}
            category={subcategory}
            index={subIndex}
            level={level + 1}
            parentIndex={index}
            grandParentIndex={parentIndex}
            categories={categories}
            setCategories={setCategories}
            visibleColumns={visibleColumns}
            DeleteCategory={DeleteCategory}
            themeColor={themeColor}
          />
        ))}
    </>
  );
};

export default RenderCategoryRow;
